import axios from 'axios';
import { openSnackbar } from 'api/snackbar';
import { BASE_URL } from 'utils/baseUrlProvider';
import { isOTPApprovalError } from 'utils/errorHandler';
const axiosClient = axios.create({ baseURL: BASE_URL || 'http://localhost:3010/', timeout: 300000 });
// For unauthenticated public pages (OPD booking): no staff token, no refresh/login redirect, no global snackbar.
export const publicAxiosClient = axios.create({ baseURL: BASE_URL || 'http://localhost:3010/', timeout: 300000 });
let refreshRequest = null;

const clearSessionAndRedirect = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('refreshToken');
  delete axiosClient.defaults.headers.common['Authorization'];
  if (!window.location.href.includes('/login')) {
    window.location.pathname = '/login';
  }
};

const refreshAccessToken = () => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    return Promise.reject(new Error('No refresh token'));
  }

  if (!refreshRequest) {
    refreshRequest = axios
      .post(`${BASE_URL}/users/refresh/`, { refresh: refreshToken })
      .then((response) => {
        const accessToken = response.data.access;
        localStorage.setItem('authToken', accessToken);
        axiosClient.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        return accessToken;
      })
      .finally(() => {
        refreshRequest = null;
      });
  }

  return refreshRequest;
};

const isTokenExpiringSoon = (token, leewaySeconds = 60) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return !payload.exp || payload.exp * 1000 < Date.now() + leewaySeconds * 1000;
  } catch {
    return true;
  }
};

/**
 * Returns an access token that is valid for at least the next minute, refreshing it if needed.
 * Used by WebSocket connections, which cannot rely on the 401 retry interceptor below.
 */
export const getValidAccessToken = async () => {
  const accessToken = localStorage.getItem('authToken');
  if (accessToken && !isTokenExpiringSoon(accessToken)) {
    return accessToken;
  }

  try {
    return await refreshAccessToken();
  } catch {
    return accessToken;
  }
};

// ==============================|| AXIOS - FOR MOCK SERVICES ||============================== //

axiosClient.interceptors.request.use(
  async (config) => {
    const accessToken = localStorage.getItem('authToken');
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      const refreshToken = localStorage.getItem('refreshToken');

      if (refreshToken) {
        originalRequest._retry = true;
        try {
          const accessToken = await refreshAccessToken();
          originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
          return axiosClient(originalRequest);
        } catch (refreshError) {
          clearSessionAndRedirect();
          return Promise.reject(refreshError);
        }
      }

      clearSessionAndRedirect();
    }

    const errorMessages = {
      default: 'Network error or incorrect URL. Please check your connection.',
      401: 'Unauthorized access. Please log in again.',
      413: 'File size is too large. Maximum allowed size is 100 MB after compression.',
    };

    // Check if it's the intended OTP approval error - don't show snackbar for this
    const isOTPError = isOTPApprovalError(error);
    const skipErrorSnackbar = error.config?.skipErrorSnackbar
      || error.config?.skipErrorSnackbarStatuses?.includes(error.response?.status);

    const message = error.response 
      ? (errorMessages[error.response.status] || `Error ${error.response.status}: ${error.response.statusText}`)
      : errorMessages.default;

    // Only show snackbar if it's not the intended OTP approval error
    if (!isOTPError && !skipErrorSnackbar) {
      openSnackbar({
        open: true,
        message,
        variant: 'alert',
        alert: { 
          color: error.response?.status === 401 ? 'warning' : 'error', 
          variant: 'filled' 
        },
        transition: 'SlideUp'
      });
    }

    return Promise.reject(error);
  }
);

export default axiosClient;

export const fetcher = async (args) => {
  const [url, config] = Array.isArray(args) ? args : [args];

    const res = await axiosClient.get(url, { ...config });

    return res.data;
};
